/**
 * Create Board from Template Form
 *
 * Allows users to create a new board by selecting an existing template.
 * Follows the same UI pattern as CreateBoardForm for consistency.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AsyncButton } from '@/components/ui';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { sizing, radius, spacing } from '@/styles/design-tokens';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: GET_BOARD_TEMPLATES is a silent background fetch to populate the template list
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useUserGroupsQuery } from '@/hooks/queries';
import { logger } from '@/utils/logger';
import type {
  GetBoardTemplatesInput,
  GetBoardTemplatesOutput,
  CreateBoardFromTemplateInput,
  CreateBoardFromTemplateOutput,
} from '@/types/generated';
import type { GetBoardTemplatesTemplatesItem } from '@/types/generated/processor-types';
import TemplatePreview from '@/components/core/Kanban/TemplatePreview';

interface CreateBoardFromTemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (boardId: string) => void;
}

export default function CreateBoardFromTemplateForm({
  isOpen,
  onClose,
  onSuccess,
}: CreateBoardFromTemplateFormProps) {
  const colors = useThemeColors();
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const [boardTitle, setBoardTitle] = useState('');
  const [isGroupBoard, setIsGroupBoard] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] =
    useState<GetBoardTemplatesTemplatesItem | null>(null);
  const [templates, setTemplates] = useState<GetBoardTemplatesTemplatesItem[]>(
    []
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { execute: executeCreateBoard, isLoading: isCreating } =
    useOrchestrator<
      CreateBoardFromTemplateInput,
      CreateBoardFromTemplateOutput
    >(Action.CREATE_BOARD_FROM_TEMPLATE);

  const { data: groupsData, isLoading: isLoadingGroups } = useUserGroupsQuery(
    isGroupBoard ? activeOrganizationId : undefined
  );
  const groups = groupsData?.groups ?? [];

  const fetchTemplates = useCallback(async () => {
    if (!user) return;

    setIsLoadingTemplates(true);
    try {
      // organization_id is auto-injected by ActionOrchestrator before validation
      const result = await actionOrchestrator.execute<
        GetBoardTemplatesInput,
        GetBoardTemplatesOutput
      >(Action.GET_BOARD_TEMPLATES, {} as GetBoardTemplatesInput);

      if (result.code === ResponseCode.SUCCESS && result.data) {
        setTemplates(result.data.templates);
      } else {
        logger.error('Failed to fetch templates', { code: result.code });
      }
    } catch (err) {
      logger.error('Error fetching templates', { error: err });
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [user]);

  // Reset form and fetch templates when dialog opens
  useEffect(() => {
    if (isOpen) {
      setBoardTitle('');
      setIsGroupBoard(false);
      setSelectedGroupId('');
      setSelectedTemplate(null);
      setErrors({});
      fetchTemplates();
    }
  }, [isOpen, fetchTemplates]);

  const isValid =
    selectedTemplate !== null &&
    boardTitle.trim().length > 0 &&
    boardTitle.trim().length <= 100 &&
    (!isGroupBoard || !!selectedGroupId);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (!isValid || !selectedTemplate || !user) return;

      const result = await executeCreateBoard({
        template_id: selectedTemplate.id,
        board_title: boardTitle.trim(),
        owner_id: user.id,
        group_id: isGroupBoard ? selectedGroupId : undefined,
      });

      if (result.code === ResponseCode.SUCCESS && result.data) {
        onSuccess(result.data.board_id);
        onClose();
      }
    },
    [
      boardTitle,
      isGroupBoard,
      selectedGroupId,
      selectedTemplate,
      user,
      isValid,
      onSuccess,
      onClose,
      executeCreateBoard,
    ]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isCreating) {
      onClose();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={isCreating ? undefined : onClose}>
      <DialogContent
        className="max-w-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}
        onKeyDown={handleKeyDown}
      >
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-2 -mx-2">
            <div className="space-y-4">
              {/* Header */}
              <div>
                <h3
                  className="font-semibold text-lg mb-2 flex items-center gap-2"
                  style={{ color: colors.text.primary }}
                >
                  <Icon
                    name={IconName.LAYOUT_TEMPLATE}
                    size="lg"
                    style={{ color: colors.primary }}
                  />
                  Create from Template
                </h3>
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  Create a new board using an existing template's structure
                </p>
              </div>

              {/* Template Picker */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.text.primary }}
                >
                  Select Template <span style={{ color: colors.error }}>*</span>
                </label>

                {isLoadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon
                      name={IconName.LOADER}
                      size="md"
                      className="animate-spin"
                      style={{ color: colors.text.secondary }}
                    />
                    <span
                      className="ml-2 text-sm"
                      style={{ color: colors.text.secondary }}
                    >
                      Loading templates...
                    </span>
                  </div>
                ) : templates.length === 0 ? (
                  <div
                    className="px-4 py-8 text-center border rounded-lg"
                    style={{
                      borderColor: colors.border.default,
                      backgroundColor: colors.offWhite,
                    }}
                  >
                    <Icon
                      name={IconName.LAYOUT_TEMPLATE}
                      size="xl"
                      style={{ color: colors.text.tertiary }}
                    />
                    <p
                      className="mt-2 text-sm"
                      style={{ color: colors.text.secondary }}
                    >
                      No templates available.
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: colors.text.tertiary }}
                    >
                      Save a board as template first from Board Settings.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {templates.map(template => {
                      const isSelected = selectedTemplate?.id === template.id;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => setSelectedTemplate(template)}
                          className="w-full text-left px-4 py-3 border transition-all"
                          style={{
                            borderColor: isSelected
                              ? colors.primary
                              : colors.border.default,
                            backgroundColor: isSelected
                              ? colors.offWhite
                              : colors.background,
                            borderRadius: radius.md,
                            borderWidth: isSelected ? '2px' : '1px',
                          }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className="font-semibold text-sm"
                              style={{
                                color: isSelected
                                  ? colors.primary
                                  : colors.text.primary,
                              }}
                            >
                              {template.name}
                            </span>
                            {isSelected && (
                              <Icon
                                name={IconName.CHECK}
                                size="sm"
                                style={{ color: colors.primary }}
                              />
                            )}
                          </div>
                          {template.description && (
                            <p
                              className="text-xs mb-2"
                              style={{ color: colors.text.secondary }}
                            >
                              {template.description}
                            </p>
                          )}
                          {/* Column preview pills */}
                          <div className="flex flex-wrap gap-1">
                            {template.statuses
                              .sort((a, b) => a.position - b.position)
                              .map(status => {
                                const pillColor =
                                  status.color || colors.text.secondary;
                                return (
                                  <span
                                    key={status.id}
                                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded"
                                    style={{
                                      backgroundColor: pillColor + '20',
                                      color: pillColor,
                                      border: `1px solid ${pillColor}40`,
                                    }}
                                  >
                                    {status.name}
                                  </span>
                                );
                              })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Template Preview */}
              {selectedTemplate && (
                <div
                  className="border rounded-lg overflow-hidden"
                  style={{
                    borderColor: colors.border.default,
                    backgroundColor: colors.offWhite,
                    padding: '0 12px',
                  }}
                >
                  <TemplatePreview template={selectedTemplate} compact />
                </div>
              )}

              {/* Board Title Input */}
              <div>
                <label
                  htmlFor="template-board-title"
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.text.primary }}
                >
                  Board Title <span style={{ color: colors.error }}>*</span>
                </label>
                <input
                  id="template-board-title"
                  type="text"
                  value={boardTitle}
                  onChange={e => setBoardTitle(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  maxLength={100}
                  disabled={isCreating}
                  className="w-full px-3 transition-colors"
                  style={{
                    backgroundColor: colors.offWhite,
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: errors.boardTitle
                      ? colors.error
                      : colors.border.default,
                    color: colors.text.primary,
                    borderRadius: radius.md,
                    height: sizing.input.md,
                    outline: 'none',
                  }}
                  onFocus={e => {
                    if (!errors.boardTitle) {
                      e.currentTarget.style.borderColor = colors.primary;
                    }
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = errors.boardTitle
                      ? colors.error
                      : colors.border.default;
                  }}
                  placeholder="Enter board title"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs" style={{ color: colors.error }}>
                    {errors.boardTitle || ''}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: colors.text.secondary }}
                  >
                    {boardTitle.length}/100
                  </span>
                </div>
              </div>

              {/* Group Board Toggle */}
              <div
                className="flex items-center gap-3 border"
                style={{
                  backgroundColor: colors.offWhite,
                  borderColor: colors.border.default,
                  borderRadius: radius.md,
                  padding: `${spacing.xs} ${spacing.sm}`,
                }}
              >
                <input
                  id="template-is-group-board"
                  type="checkbox"
                  checked={isGroupBoard}
                  onChange={e => setIsGroupBoard(e.target.checked)}
                  disabled={isCreating}
                  style={{
                    cursor: isCreating ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                  }}
                />
                <label
                  htmlFor="template-is-group-board"
                  className="text-sm font-medium flex-1"
                  style={{
                    color: colors.text.primary,
                    cursor: isCreating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'calc(2px * var(--sizing-multiplier, 1))',
                  }}
                >
                  <span>Group Board</span>
                  <span
                    className="text-xs font-normal"
                    style={{ color: colors.text.secondary }}
                  >
                    Group boards are shared with your organization
                  </span>
                </label>
              </div>

              {/* Group Selector */}
              {isGroupBoard && (
                <div>
                  <label
                    htmlFor="template-group-select"
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.text.primary }}
                  >
                    Select Group <span style={{ color: colors.error }}>*</span>
                  </label>
                  {isLoadingGroups ? (
                    <div className="flex items-center gap-2 py-2">
                      <Icon
                        name={IconName.LOADER}
                        size="sm"
                        className="animate-spin"
                        style={{ color: colors.text.secondary }}
                      />
                      <span
                        className="text-sm"
                        style={{ color: colors.text.secondary }}
                      >
                        Loading groups...
                      </span>
                    </div>
                  ) : (
                    <select
                      id="template-group-select"
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      disabled={isCreating}
                      className="w-full px-3"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: colors.border.default,
                        color: selectedGroupId
                          ? colors.text.primary
                          : colors.text.secondary,
                        borderRadius: radius.md,
                        height: sizing.input.md,
                        outline: 'none',
                      }}
                    >
                      <option value="">Select a group...</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            className="flex justify-between pt-4 border-t mt-4"
            style={{ borderColor: colors.border.default }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="px-4 py-2 font-medium"
              style={{
                backgroundColor: colors.background,
                color: colors.text.primary,
                border: `1px solid ${colors.border.default}`,
                borderRadius: radius.DEFAULT,
                height: sizing.button.md,
                opacity: isCreating ? 0.5 : 1,
                cursor: isCreating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = colors.offWhite;
                  e.currentTarget.style.borderColor = colors.text.secondary;
                }
              }}
              onMouseLeave={e => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = colors.background;
                  e.currentTarget.style.borderColor = colors.border.default;
                }
              }}
            >
              Cancel
            </button>
            <AsyncButton
              onClick={handleSubmit}
              disabled={!isValid || isCreating}
              isLoading={isCreating}
              icon={IconName.LAYOUT_TEMPLATE}
              loadingText="Creating..."
              className="px-6"
            >
              Create Board
            </AsyncButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
